import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import { Container } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import { Small, Medium, LargeUp } from 'components/MediaQuery';
import { small, mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

const BUTTON_MIN_WIDTH_DESKTOP = 245;

const PageSectionCTAImages = ({ heading, ctaImages, trackEvent }) => {
  return (
    <Container>
      <div className="PageSectionCTAImages">
        {!!heading && (
          <SecondaryHeading className="PageSectionCTAImages__heading">{heading}</SecondaryHeading>
        )}
        <div className="PageSectionCTAImages__wrapper">
          {map(ctaImages, ({ ctaLink, imagePortrait, imageLandscape }, index) => (
            <div key={index} className="PageSectionCTAImages__image">
              <Small>
                <Image
                  src={get(imagePortrait, ['file', 'url'])}
                  alt={get(imagePortrait, 'description')}
                  width={get(imagePortrait, ['file', 'details', 'image', 'width'])}
                  height={get(imagePortrait, ['file', 'details', 'image', 'height'])}
                />
              </Small>
              <Medium>
                <Image
                  src={get(imagePortrait, ['file', 'url'])}
                  alt={get(imagePortrait, 'description')}
                  width={get(imagePortrait, ['file', 'details', 'image', 'width'])}
                  height={get(imagePortrait, ['file', 'details', 'image', 'height'])}
                />
              </Medium>
              <LargeUp>
                <Image
                  src={get(imageLandscape, ['file', 'url'])}
                  alt={get(imageLandscape, 'description')}
                  width={get(imageLandscape, ['file', 'details', 'image', 'width'])}
                  height={get(imageLandscape, ['file', 'details', 'image', 'height'])}
                />
              </LargeUp>
              <div className="PageSectionCTAImages__overlay">
                <Button
                  className="PageSectionCTAImages__link"
                  linkTo={get(ctaLink, 'url')}
                  type={TYPES.INVERSE}
                  onClick={() =>
                    trackEvent({
                      eventAction: 'Shop Card - clickShopCTA',
                      eventLabel: get(ctaLink, 'text'),
                    })
                  }
                >
                  {get(ctaLink, 'text')}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <style jsx>{`
          @media ${mediumUp} {
            .PageSectionCTAImages {
              padding: 0 ${Theme.get('spacing')}px;
              flex-direction: row;
            }
          }

          @media ${largeUp} {
            .PageSectionCTAImages {
              padding: 0 ${Theme.get('spacing') * 8}px;
            }
          }

          .PageSectionCTAImages__wrapper {
            display: flex;
            flex-direction: column;
          }

          @media ${mediumUp} {
            .PageSectionCTAImages__wrapper {
              flex-direction: row;
            }
          }

          .PageSectionCTAImages__image {
            position: relative;
            flex: 1;
          }

          @media ${mediumUp} {
            .PageSectionCTAImages__image {
              margin-right: ${Theme.get('spacing') * 2}px;
            }

            .PageSectionCTAImages__image:last-child {
              margin-right: 0;
            }
          }

          @media ${largeUp} {
            .PageSectionCTAImages__image {
              margin-right: ${Theme.get('spacing') * 4}px;
            }

            .PageSectionCTAImages__image:last-child {
              margin-right: 0;
            }
          }

          @media ${small} {
            .PageSectionCTAImages__image {
              margin-bottom: ${Theme.get('spacing') * 8}px;
            }

            .PageSectionCTAImages__image:last-child {
              margin-bottom: 0;
            }
          }

          .PageSectionCTAImages__overlay {
            position: absolute;
            bottom: 0;
            width: 100%;
            display: flex;
            justify-content: center;
          }
        `}</style>
        <style jsx global>{`
          .Button.PageSectionCTAImages__link {
            display: block;
            width: 100%;
            margin: ${Theme.get('spacing') * 6}px;
            padding: 16px ${Theme.get('spacing') * 3}px 14px;
            border-color: transparent;
          }

          @media ${largeUp} {
            .Button.PageSectionCTAImages__link {
              width: auto;
              min-width: ${BUTTON_MIN_WIDTH_DESKTOP}px;
              margin: ${Theme.get('spacing') * 10}px;
            }
          }
        `}</style>
        <style jsx global>
          {`
            .PageSectionCTAImages__heading.typography--secondary-heading {
              padding-bottom: ${Theme.get('spacing') * 5}px;
            }
          `}
        </style>
      </div>
    </Container>
  );
};

const imagePropType = PropTypes.shape({
  file: PropTypes.shape({
    url: PropTypes.string.isRequired,
    details: PropTypes.shape({
      image: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }),
    }),
  }).isRequired,
  description: PropTypes.string,
});

PageSectionCTAImages.propTypes = {
  heading: PropTypes.string,
  ctaImages: PropTypes.arrayOf(
    PropTypes.shape({
      ctaLink: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }).isRequired,
      imageLandscape: imagePropType.isRequired,
      imagePortrait: imagePropType.isRequired,
    })
  ).isRequired,
  trackEvent: PropTypes.func,
};

PageSectionCTAImages.defaultProps = {
  heading: '',
  trackEvent: () => {},
};

export default PageSectionCTAImages;
